import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, History, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { StatusBadge } from "@/components/ui-kit/StatusBadge";
import { Button } from "@/components/ui-kit/Button";
import { AcsaSendDialog } from "@/components/invoices/AcsaSendDialog";
import { useFunder } from "@/funder/FunderContext";

/** Per-matter invoice history (both payer sequences) + recent audit activity. */
export function InvoiceHistory({ matterId, version }) {
  const navigate = useNavigate();
  const { payerLabel } = useFunder();
  const [invoices, setInvoices] = useState(null);
  const [audit, setAudit] = useState([]);
  const [acsaInvoice, setAcsaInvoice] = useState(null);

  const load = () => {
    api.get(`/matters/${matterId}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
    api.get(`/matters/${matterId}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, version]);

  const ordered = (invoices || []).slice().sort((a, b) => (b.issue_date || "").localeCompare(a.issue_date || ""));
  const canSendAcsa = (i) => i.payer === "ACSA" && i.snapshot && !["Void", "Superseded"].includes(i.status);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]" data-testid="invoice-history">
      <div className="rounded-lg border border-border bg-surface shadow-card">
        <div className="border-b border-border px-5 py-3"><h3 className="text-sm font-semibold text-foreground">Invoice history</h3></div>
        <div className="p-2">
          {invoices === null ? (
            <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : ordered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No invoices issued yet.</p>
          ) : (
            <Table>
              <THead><TR><TH>Number</TH><TH>Payer</TH><TH>Issued</TH><TH className="text-right">Amount due</TH><TH>Status</TH><TH className="text-right">Actions</TH></TR></THead>
              <TBody>
                {ordered.map((i) => (
                  <TR key={i.id} data-testid={`history-invoice-${i.id}`} className="cursor-pointer" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <TD className="font-medium text-foreground">{i.number || "Draft"}</TD>
                    <TD className="text-muted-foreground">{payerLabel(i.payer)}</TD>
                    <TD className="text-muted-foreground">{i.issue_date ? shortDate(i.issue_date) : "—"}</TD>
                    <TD className="nums text-right">{i.snapshot ? money(i.snapshot.amount_due) : "—"}</TD>
                    <TD><StatusBadge status={i.status} /></TD>
                    <TD className="text-right">
                      {canSendAcsa(i) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid={`history-send-acsa-${i.id}`}
                          onClick={(e) => { e.stopPropagation(); setAcsaInvoice(i); }}
                        >
                          <Landmark className="h-3.5 w-3.5" /> {i.acsa_submitted_at ? "Resend" : "Send to ACSA"}
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          <History className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Activity</h3>
        </div>
        <div className="max-h-72 space-y-2 overflow-y-auto p-4" data-testid="matter-audit">
          {audit.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            audit.map((a) => (
              <div key={a.id} className="border-l-2 border-border pl-3" data-testid={`audit-${a.id}`}>
                <p className="text-sm text-foreground">{a.message}</p>
                <p className="text-xs text-muted-foreground">{shortDate(a.timestamp)} · {a.actor || "system"}</p>
              </div>
            ))
          )}
        </div>
      </div>

      <AcsaSendDialog
        open={!!acsaInvoice}
        onOpenChange={(o) => { if (!o) setAcsaInvoice(null); }}
        invoice={acsaInvoice}
        onDone={load}
      />
    </div>
  );
}
